import { SidePanelRouter } from "@/app/router/SidePanelRouter";
import { SplitView } from "@/components/layout";
import { Sidebar } from "@/components/layout/components/sidebar/sidebar";
import { TopBar } from "@/components/layout/components/top-bar";
import { MDSMediaQueries } from "@/mds/foundations";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useAuthenticatedMetrics } from "@/app/outlets/layout/hooks";
import { StandardModeAppOverlays } from "@/app/outlets/layout/standard-mode-app-overlays";
import { ImportWizard } from "@/components/layout/components/sidebar/import-wizard";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { CenterPanelSplitView } from "@/components/layout/components/center-panel-split-view/center-panel-split-view";
import { DesktopEventListenersBridge } from "@/components/desktop/desktop-event-listeners-bridge";
import { ImportModalProvider, useImportModal } from "@/contexts/ImportModalContext";
import { SystemPermissionsManager } from "@/components/desktop/system-permissions";
import { SettingsModal } from "@/pages/settings/components/settings-modal";
import { AgentOverlay } from "@/pages/agent";

interface StandardModeAppLayoutOutletProps {}

const StandardModeAppLayoutOutletContent = observer(() => {
  useAuthenticatedMetrics();
  const { store } = useAppStore();
  const { isImportModalOpen, setIsImportModalOpen, closeImportModal } = useImportModal();

  const currentSidebarVariant = store.layout.currentSidebarVariant;
  const shouldHideSidebarToggle = store.layout.shouldHideSidebarToggle;
  const toggleSidebar = store.interface.toggleSidebar;

  useEffectOnMount(() => {
    // The sidebar state starts as closed. Determine if it should be opened on load.
    if (store.interface.isSidebarOpen) return;
    if (store.layout.hasRoomForSidebarOnInitialLoad) {
      store.interface.setIsSidebarOpen(true, { animate: false });
    }
  });

  return (
    <>
      <ImportWizard isOpen={isImportModalOpen} onClose={closeImportModal} />
      <StandardModeAppOverlays />
      <SystemPermissionsManager />
      <SettingsModal />
      <AgentOverlay />
      <DesktopEventListenersBridge />
      <OuterWrapper data-component="OuterWrapper">
        <Sidebar
          currentSidebarVariant={currentSidebarVariant}
          setIsImportModalOpen={setIsImportModalOpen}
          shouldHideSidebarToggle={shouldHideSidebarToggle}
          toggleSidebar={toggleSidebar}
          data-component="Sidebar"
        />
        <InnerWrapper data-component="InnerWrapper">
          <TopBar
            setIsImportModalOpen={setIsImportModalOpen}
            currentSidebarVariant={currentSidebarVariant}
            data-component="TopBar"
          />
          <Main data-component="Main">
            <SplitView sidePanel={<SidePanelRouter />}>
              <CenterPanelSplitView data-component="CenterPanelSplitView" />
            </SplitView>
          </Main>
        </InnerWrapper>
      </OuterWrapper>
    </>
  );
});

export const StandardModeAppLayoutOutlet = observer<StandardModeAppLayoutOutletProps>(() => {
  return (
    <ImportModalProvider>
      <StandardModeAppLayoutOutletContent />
    </ImportModalProvider>
  );
});

const OuterWrapper = styled.div`
  height: 100%;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.colors.surface.chrome};
  min-height: 0;
`;
OuterWrapper.displayName = "OuterWrapper";

const InnerWrapper = styled.div({
  height: "100%",
  width: "100%",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flexShrink: 1,
  minHeight: 0,
});
InnerWrapper.displayName = "InnerWrapper";

const Main = styled("div")({
  height: "100%",
  width: "100%",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  flexShrink: 1,
  minHeight: 0,
  [MDSMediaQueries.rules.tablet]: {
    paddingLeft: 0,
  },
  [MDSMediaQueries.rules.desktop]: {
    paddingLeft: 0,
  },
});
Main.displayName = "Main";
